import { getSessionId } from '@aspendental/shared-utils/utils';
import { Box } from '@mui/material';
import * as Sentry from '@sentry/nextjs';
import { NextPage } from 'next';
import type { AppProps, NextWebVitalsMetric } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { appWithTranslation } from 'next-i18next';
import { ReactElement, ReactNode, useEffect } from 'react';

import '@contentful/live-preview/style.css';
import { Session } from '@shared-components-web';
import { TagThemeType } from '@shared-components-web/types';
import { PHONE_OTP_RECAPTCHA_CONTAINER_ID } from '@src/auth-firebase/firebase-client';
import { FirebaseProvider } from '@src/auth-firebase/firebase-context';
import { ContextProviders } from '@src/components/shared/providers/ContextProviders';
import { initializeLogRocket } from '@src/components/shared/third-party/logRocket';
import Layout from '@src/components/templates/layout';
import { getLogger } from '@src/lib/logging/logger';
import { isSentryEnabled, toggleSentry } from '@src/services/sentry';
import getConfigByBrand from '@src/utils/get-config-by-brand';
import { initializeSentryClient } from '@src/utils/sentryClientInit';
import i18nConfig from 'next-i18next.config';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css/pagination';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css/navigation';

export type NextPageWithLayout<P = object, IP = P> = NextPage<P, IP> & {
	getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
	Component: NextPageWithLayout;
};

export function reportWebVitals({ id, name, label, value }: NextWebVitalsMetric) {
	const logger = getLogger('client');
	logger.info({
		name,
		category: label === 'web-vital' ? 'Web Vitals' : 'Next.js custom metric',
		value: Math.round(name === 'CLS' ? value * 1000 : value), // values must be integers
		id, // id unique to current page load
	});
}

declare global {
	interface Window {
		VWO: {
			push: (args: any[]) => void;
		};
		LogRocket: {
			track: (eventName: string, eventData: any) => void;
		};
	}
}

const App = ({ Component, pageProps }: AppPropsWithLayout) => {
	const router = useRouter();

	const fallbackTagTheme = (process.env.TAG_BRAND_THEME || 'lovet') as TagThemeType;
	const config = (pageProps?.config as BrandThemeConfig | undefined) ?? getConfigByBrand(fallbackTagTheme);
	const brandName = config?.name || 'Lovet Pet Health Care';
	toggleSentry(!!config?.features?.sentry?.enabled);
	const environment = pageProps.env?.ENVIRONMENT_NAME || 'development';

	useEffect(() => {
		if (isSentryEnabled()) {
			if (!Sentry.isInitialized()) {
				initializeSentryClient(environment);
			}
			Sentry.setTag('brand', brandName);
			Sentry.setContext('user', {
				sessionId: getSessionId(),
				brand: brandName,
			});
		}
	}, [brandName, environment]);

	useEffect(() => {
		if (config?.features?.logRocket?.projectId) {
			initializeLogRocket(environment, config?.features?.logRocket?.projectId);
		}
	}, [brandName, environment, config?.features?.logRocket?.projectId]);

	useEffect(() => {
		// Adding a timeout to ensure that VWO and LogRocket are initialized before we push the VWO event
		// LogRocket is not available immediately and it takes a bit for GTM to add it in.
		const timeoutId = setTimeout(() => {
			if (globalThis?.VWO && !globalThis?.VWO[1] && globalThis?.LogRocket) {
				globalThis.VWO.push([
					'onEventReceive',
					'vA', // Variation Applied
					function (data) {
						const [eventName, experimentId, goalId] = data;
						globalThis.LogRocket.track('VWO-Lovet', {
							eventName: eventName ?? '',
							experimentId: experimentId ?? '',
							goalId: goalId ?? '',
						});
					},
				]);
			} else {
				console.warn(
					'VWO or LogRocket is not available',
					Boolean(globalThis?.VWO),
					Boolean(globalThis?.LogRocket)
				);
			}
		}, 5000);
		return () => clearTimeout(timeoutId);
	}, []);

	if (router.pathname === '_error') return <Component {...pageProps} />;
	const features = config?.features;

	const getLayout =
		Component.getLayout ??
		((page) => {
			return (
				<Layout
					appData={pageProps.appData}
					withSimpleLayout={features?.footer?.withSimpleLayout ?? false}
					footerData={pageProps.footerData}
					logoHref={
						pageProps.env?.ENVIRONMENT_NAME === 'prod'
							? config?.features?.livePreview?.prod
							: config?.features?.livePreview?.stage
					}
				>
					{page}
				</Layout>
			);
		});

	return (
		<Session>
			<Head>
				<meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
			</Head>
			<ContextProviders
				config={config}
				preview={pageProps.preview}
				environment={pageProps.env?.ENVIRONMENT_NAME}
				user={pageProps.user}
			>
				{/* shared target for Firebase phone OTP invisible reCAPTCHA across the app */}
				<Box id={PHONE_OTP_RECAPTCHA_CONTAINER_ID} sx={{ position: 'absolute', minHeight: 1 }} />
				<FirebaseProvider config={pageProps.firebaseConfig ?? {}}>
					{getLayout(<Component {...pageProps} />)}
				</FirebaseProvider>
			</ContextProviders>
		</Session>
	);
};

export default appWithTranslation(App, i18nConfig);
